import { Validators } from '@angular/forms';
import {
  BaseColumn,
  ControlName,
  Pagination,
  PrgMissingTranslationHandler,
  TableConfigs,
} from '@prg/prg-core-lib';
import { NgxLoggerLevel } from 'ngx-logger';

import packageJson from '../../package.json';

const DEFAULT_CACHE_TIME = 3600;
export const commonEnv = {
  version: packageJson.version,
  production: false,
  useMockServices: true,
  logLevel: NgxLoggerLevel.TRACE,
  serverLogLevel: NgxLoggerLevel.OFF,

  coreModuleConfigurations: {
    baseUrl: 'https://localhost:7226/',
    urlApiPath: '/api',
    authApiPaths: {
      login: 'Authentication',
      tokenValidation: 'Authentication',
      register: 'Authentication/Register',
      simpleRegister: 'Authentication/SimpleRegister',
      confirmationAccount: 'Authentication/ConfirmationAccount',
      emailConfirmation: 'Authentication/EmailConfirmation',
      requestPassword: 'Authentication/RequestPasswordReset',
      resetPassword: 'Authentication/ResetPassword',
    },
    queryApiPaths: {
      create: 'Query',
      update: 'Query',
      getAll: 'Query/list',
      getById: 'Query/ById',
      executeQuery: 'Query/ExecuteQuery',
    },
    lookupTableApiPaths: {
      create: 'LookupTable',
      getAll: 'LookupTable/Filtered',
      getLookupTableByIdWithItems: 'LookupTable/LookupTableByIdWithItems',
      items: {
        add: 'LookupTable/AddLookupItem',
      },
    },
    userManagementApiPaths: {
      getUserById: 'User/ByIdWithRolesScopes',
      getUsersByRole: 'User/ByRole',
      createUser: 'User',
      updateUser: 'User/UpdateRolesAndScopes',
      getAllRoles: 'Role/WithScopes',
      createRole: 'Role',
      updateRole: 'Role',
    },
    entityTypeApiPaths: {
      getAll: 'EntityType',
      createEntityType: 'EntityType',
      updateEntityTypePut: 'EntityType',
      getAllEntityTypeWithOperation: 'EntityType/EntityTypeListWithOperation',
      entityTypeByName: 'EntityType/ByName',
      properties: {
        createProperty: 'EntityTypeProperty',
        updatePropertyPut: 'EntityTypeProperty',
        getAllByEntityTypeId: 'EntityTypeProperty/AllPropertiesByEntityTypeId',
        updateOrder: 'EntityTypeProperty/UpdateEntityTypePropertiesOrder',
      },
      attributes: {
        createAttribute: 'EntityTypeAttribute',
        updateAttributePut: 'EntityTypeAttribute',
        getAllByEntityTypeId: 'EntityTypeAttribute/AllAttributesByEntityTypeId',
        updateOrder: 'EntityTypeAttribute/UpdateEntityTypeAttributesORder',
      },
      operations: {
        createOperation: 'EntityTypeOperation',
        updateOperationPut: 'EntityTypeOperation',
        getAllByEntityTypeId: 'EntityTypeOperation/AllOperationsByEntityTypeId',
      },
    },
    stateModelApiPaths: {
      create: 'statemodel',
      update: 'statemodel',
      delete: 'statemodel',
      getAll: 'statemodel',
      getById: 'statemodel/byid',
      getByName: 'statemodel/byname',
      saveStateModelState: 'statemodel/savestatemodelstate',
      stateModelStatesOptions: 'statemodel/stateModelStatesOptions',
    },
    dashboardApiPaths: {
      create: 'dashboard',
      getById: 'dashboard/ById',
      getByName: 'dashboard/ByName',
      updateDashboard: 'dashboard/UpdateDashboard',
      getByUserId: 'dashboard/ByUserId',
    },
    configurationItemsApiPaths: {
      getAll: 'ConfigurationItem/list',
      updateMultipleDefaults: 'ConfigurationItem/UpdateDefaults/Multiple',
      updateMultiple: 'ConfigurationItem/Update/Multiple',
      getBypath: 'ConfigurationItem/ByPath',
    },
    dynamicForm: {
      formValueChangeDebounce: 500,
      inputValueChangeDebounce: 500,
    },
  },

  authModuleConfigurations: {
    useMockServices: false,
    authRoutingBasePath: 'auth',
    authRoutingSignupPath: 'signup',
    authRoutingLoginPath: 'login',
    authRoutingRequestPasswordPath: 'request-password',
    authRoutingResetPasswordPath: 'reset-password',
    authRoutingLoginOkRedirectPath: '',
    authRoutingRgpdPath: 'rgpd',
    authRoutingLogoutPath: 'logout',
    termsOfUseRoutingPath: 'terms-of-use',
    authStorage: {
      storage: window.localStorage,
      keyStoredUserName: 'savedUserEmail',
      keyLoggedUser: 'loggedUser',
    },
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },
  termsModuleConfigurations: {
    useMockServices: true,
    baseTermsTranslationKey: 'components.terms-conditions',
    termsOfUseRoutingPath: 'terms-of-use',
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },

  dashboardModuleConfigurations: {
    useMockServices: false,
    dashboardRoutingBasePath: 'dashboard',
    dashboardConfigRoutingPath: 'config',
    dashboardDisplayRoutingPath: 'display',
    displayDashboard: 'view',
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },

  configurationItemsModuleConfigurations: {
    useMockServices: false,
    configurationItemsRoutingBasePath: 'config-items',
    configurationItemsRoutingAdminPath: 'admin',
    configurationItemsRoutingUserPath: 'user-preferences',
    configurationItemsRoutingNotificationsPath: 'notification-types',

    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },

  dynamicFormsConfigurations: {
    useMockServices: true,
    dynamicFormsBasePath: 'dynamic-forms',
  },

  entityTypeConfigurations: {
    useMockServices: false,
    useDefaultRouting: true,
    entityTypeBasePath: 'entity-types',
    entityTypeTableColumns: [
      new BaseColumn({
        value: null,
        readonly: true,
        key: 'name',
        validators: [],
        basePathTranslation: 'entities',
      }),
      new BaseColumn({
        value: null,
        readonly: true,
        key: 'guiSettings',
        validators: [],
      }),
      new BaseColumn({
        value: null,
        readonly: true,
        key: 'universalState',
        validators: [],
      }),
    ],

    specificEntityTypeListConfigs: new TableConfigs({
      defaultColumns: ['id', 'name', 'stock', 'expiryDate'],
      globalFilterFields: ['name', 'description'],
      pagination: new Pagination(),
      multiSortMeta: [
        { field: 'name', order: -1 },
        { field: 'stock', order: -1 },
      ],
    }),
    cacheTime: DEFAULT_CACHE_TIME,
  },
  lookupTableConfigurations: {
    useMockServices: false,
    lookupTableBasePath: 'lookup-table',
    lookupTableColumns: [
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'name',
        validators: [Validators.required],
        readonly: true,
      }),
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'description',
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
        ],
      }),
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'universalState',
        validators: [Validators.required],
      }),
    ],
    lookupTableItemsColumns: [
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'name',
        validators: [Validators.required],
        readonly: true,
      }),
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'description',
        validators: [Validators.required],
      }),
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'universalState',
        validators: [],
      }),
    ],
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },
  queriesModuleConfigurations: {
    useMockServices: false,
    queriesRoutingPath: 'queries',
    queryTableColumns: [
      new BaseColumn({
        key: 'name',
        readonly: true,
        basePathTranslation: 'queries.fields',
      }),
      new BaseColumn({
        key: 'description',
        readonly: true,
        basePathTranslation: 'queries.fields',
      }),
      /*      new BaseColumn({
        key: 'isRaw',
        readonly: true,
        basePathTranslation: 'queries.fields',
      }),*/
      new BaseColumn({
        key: 'createdBy',
        readonly: true,
        basePathTranslation: 'queries.fields',
      }),
      /*    new BaseColumn({
        key: 'entityTypeId',
        readonly: true,
        basePathTranslation: 'queries.fields',
      }),*/
    ],
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },
  stateModelsConfigurations: {
    useMockServices: false,
    stateModelsBasePath: 'state-models',
    stateModelsTableColumns: [
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'name',
        validators: [Validators.required],
        readonly: true,
      }),
      new BaseColumn({
        controlName: ControlName.inputText,
        key: 'universalState',
        validators: [Validators.required],
        readonly: true,
      }),
    ],
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },
  userManagementModuleConfigurations: {
    useMockServices: false,
    userManagementBasePath: 'user-management',
    baseScopes: ['view', 'create', 'update', 'delete'],
    otherScopes: 'other',
    useDefaultRouting: true,
    cacheTime: DEFAULT_CACHE_TIME,
  },
  translations: {
    defaultMissingTranslateClassHandler: PrgMissingTranslationHandler,
  },
};

/*
 * For easier debugging in development mode, you can import the following file
 * to ignore zone related error stack frames such as `zone.run`, `zoneDelegate.invokeTask`.
 *
 * This import should be commented out in production mode because it will have a negative impact
 * on performance if an error is thrown.
 */
// import 'zone.js/dist/zone-error';  // Included with Angular CLI.
